import { IColumn, IntegrationRuntime, IOriginDataTypeName, LinkedService, MappingType, ModelMarker, Named } from 'shell-models-lib';
import { DataFlowColumn } from 'app/model/dataset/dataflowcolumn';
import { AnyDataFlowType, DecimalDataFlowType, IDataFlowDataType } from 'app/model/dataflow/dataflow-datatype';
import { ISelectGroupedOption, ISelectOption } from 'app/interfaces/ISelectOption';
import { ITransformationMethodOption } from 'app/model/mapping-artifact/mappingCanvasInterfaces';
import { DataFlowExpressionFunctions } from 'app/model/dataflow/dataflow-expression-functions';
import { DataMapperMapping } from 'app/model/adfDataMapper/dataMapperMapping';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { AdfDataMapperAttributeAutomap } from 'app/model/adfDataMapper/automap/adfDataMapperAttribute.automap';
import { LoggerService } from 'app/logger/logger.service';
import { AdfDataMapperTableAutomap } from 'app/model/adfDataMapper/automap/adfDataMapperTable.automap';
import { MappingTargetConnectionInfo } from 'app/model/adfDataMapper/mappingTargetConnectionInfo';
import { ModelService } from 'app/model/services/model.service';
import { getVnetIRFromLinkedService } from 'app/extension/api/integrationruntime-vnet-api';
import { Message, MessageKind } from 'app/services/messaging/message';
import { ModelMarkerProvider } from 'app/authoring/component/transformations/transformation-expression-builder/model.marker.provider';
import { IDataFlowArguments, isTruthy } from '../dataflow/dataflow-expression-utilities';
import { DatasetFormatType } from '../dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpressionUtils } from '../adapters/dataflow/expression.util';
import { MappingSourceConnectionInfo } from './mappingSourceConnectionInfo';
import * as tableUtils from 'app/model/adfDataMapper/mapperTable.utils';
import { EventHubsSettings } from '../dataflow/dataset-settings/event-hubs-settings';
export const streamingDatasetFormats = new Set<DatasetFormatType>([DatasetFormatType.EventHubs, DatasetFormatType.Kafka]);
const supportedPrimaryKeyTargetOptions = new Set([
	DatasetFormatType.Delta,
	DatasetFormatType.SqlDB,
	DatasetFormatType.SynapseAnalytics,
]);

export function isColumnLevelMappingsCanvasEnabled(
	dataMapperMapping: DataMapperMapping): boolean {
	if (!!dataMapperMapping
		&& dataMapperMapping.isValidMapping()) {
		return !dataMapperMapping.isSourceTargetDrifted() && tableUtils.areSourceTargetTableSchemasLoaded(dataMapperMapping.sourceTable, dataMapperMapping.targetTable);
	}

	return false;
}

export function getReturnDataTypeForExpression(expression: string): IDataFlowDataType {
	if (!!expression) {
		const availableArguments: IDataFlowArguments = {
			dataFlowColumns: [],
			dataFlowFunctions: DataFlowExpressionFunctions.ALL_FUNCTIONS,
			dataFlowParameters: [],
			dataFlowTransientColumns: [],
			dataFlowCacheSinks: []
		};
		const modelMarkerProvider = new ModelMarkerProvider(availableArguments);
		modelMarkerProvider.evaluate(expression);
		return modelMarkerProvider.getExpressionReturnType();
	}

	return new AnyDataFlowType();
}

export function getQualifiedTypeString(col: DataFlowColumn): string {
	if (col.columnType === 'decimal' || col.columnType === 'complex' || col.columnType === 'array') {
		return `${col.dataFlowColumnType.typeInDsl}`;
	}

	return col.dataFlowColumnType.typeString;
}

export function getValidTransformationName(isSource: Boolean, transformationName?: string): string {
	return (isSource ? 'source' : 'sink') + transformationName?.replace(/[^A-Z0-9]/ig, '');
}

export function adjustSchemaDriftForTables(dataMapperMapping: DataMapperMapping, isAutoMapDisabled: boolean): void {
	if (!!dataMapperMapping) {
		if (dataMapperMapping.isValidMapping()) {
			if (isAutoMapDisabled) {
				tableUtils.setAllowSchemaDrift(dataMapperMapping.sourceTable, false);
				tableUtils.setAllowSchemaDrift(dataMapperMapping.targetTable, false);
			}
			// if the schemas are drifted because of any one of the table, set it true for other as well!
			else if (dataMapperMapping.isSourceTargetDrifted()) {
				tableUtils.setAllowSchemaDrift(dataMapperMapping.sourceTable, true);
				tableUtils.setAllowSchemaDrift(dataMapperMapping.targetTable, true);
			}
		}
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isValidViolationMessage(message: Message<any>, adfDataMapper: AdfDataMapper): boolean {
	return !!message
		&& !!message.payload.error
		&& !!message.payload.error.owned
		&& !!message.payload.error.context
		&& message.kind === MessageKind.ValidationError
		&& message.payload.error.owned.named === adfDataMapper;
}

export function getTransformationMethods(isContinuousMapper: boolean): Array<ISelectGroupedOption<ITransformationMethodOption>> {
	return [
		{
			label: stringResources.CommonMappings,
			items: getCommonTransformationOptions()
		},
		{
			label: stringResources.DeriveTransformationUILabel,
			items: getDerivedTransformationOptions()
		},
		...(!isContinuousMapper ? [
			{
				label: stringResources.AggregateTransformationLabel,
				items: getAggregateTransformationOptions()
			}
		] : [])
	];
}

export function automapMapTableMappingRow(
	adfDataMapper: AdfDataMapper,
	dataMapperMapping: DataMapperMapping,
	logger: LoggerService,
	fuzzyMatchLevel: number = undefined): void {

	new AdfDataMapperAttributeAutomap(
		fuzzyMatchLevel,
		adfDataMapper,
		dataMapperMapping,
		logger);
}

export function automapTablesMappingRow(
	adfDataMapper: AdfDataMapper,
	targetConnectionInfo: MappingTargetConnectionInfo,
	logger: LoggerService,
	fuzzyMatchLevel: number = undefined): void {

	new AdfDataMapperTableAutomap(
		fuzzyMatchLevel,
		adfDataMapper,
		targetConnectionInfo,
		logger);
}

export function convertToIColumn(tableName: string, column: DataFlowColumn): IColumn {
	if (column) {
		return {
			name: column.name,
			baseAttributeReference: {
				entity: tableName,
				name: column.name
			},
			originDataTypeName: toOriginTypeName(column.dataFlowColumnType)
		};
	}

	return undefined;
}

export function getNewDataMapperMapping(adfDataMapper: AdfDataMapper): DataMapperMapping {
	const dataMapperMapping = Named.create<DataMapperMapping>(DataMapperMapping.MODEL, adfDataMapper);
	dataMapperMapping.name = adfDataMapper.name;
	dataMapperMapping.attributeMappings = [];
	return dataMapperMapping;
}

export async function fetchVnetBasedIRIfAny(sourceLinkedService: LinkedService, targetLinkedService: LinkedService, modelService: ModelService): Promise<IntegrationRuntime> {
	if (isLinkedServicePublished(sourceLinkedService)) {
		await modelService.fetch(sourceLinkedService, true);
	}
	if (isLinkedServicePublished(targetLinkedService)) {
		await modelService.fetch(targetLinkedService, true);
	}
	let resVnetIr: IntegrationRuntime = null;
	await getVnetIRFromLinkedService(sourceLinkedService).then(async (srcVnetIr) => {
		if (!srcVnetIr) {
			await getVnetIRFromLinkedService(targetLinkedService).then((targetVnetIr) => {
				resVnetIr = targetVnetIr;
			});
		}
		else {
			resVnetIr = srcVnetIr;
		}
	});
	return resVnetIr;
}

export function isStreamingSource(datasetFormat: DatasetFormatType): boolean {
	return streamingDatasetFormats.has(datasetFormat);
}

export function escapeDelimiter(delimiter: string): string {
	if (DataFlowExpressionUtils.containsUnescapedTabInString(delimiter)) {
		delimiter = DataFlowExpressionUtils.escapeTabInDslString(delimiter);
	}

	if (DataFlowExpressionUtils.containsUnescapedAllNewLineInString(delimiter)) {
		delimiter = DataFlowExpressionUtils.escapeAllNewlineInDslString(delimiter);
	}

	if (DataFlowExpressionUtils.containsUnescapedHeadingStartInString(delimiter)) {
		delimiter = DataFlowExpressionUtils.escapeHeadingStartInDslString(delimiter);
	}

	if (delimiter === '\'') {
		delimiter = '\\\'';
	}

	else if (delimiter === '"') {
		delimiter = '\\\"';
	}

	else if (delimiter === '\\') {
		delimiter = '\\\\';
	}

	return delimiter;
}

export function unescapeDelimiter(delimiter: string): string {
	if (!!delimiter) {
		delimiter = DataFlowExpressionUtils.unescapeAllNewlineInDslString(delimiter);

		if (delimiter === '\\t') {
			delimiter = '\t';
		}

		if (delimiter === '\\u0001') {
			delimiter = '\u0001';
		}

		if (delimiter === '\\\'') {
			delimiter = '\'';
		}

		else if (delimiter === '\\\"') {
			delimiter = '"';
		}

		else if (delimiter === '\\\\') {
			delimiter = '\\';
		}
	}

	return delimiter;
}

export function isAutoMappingDisabled(sourceConnection: MappingSourceConnectionInfo, targetConnection: MappingTargetConnectionInfo): boolean {
	// for streaming sink, Auto mapping is disabled when header columns are included
	if (isStreamingSource(targetConnection.tables?.[0]?.datasetType)) {
		const settings = targetConnection.tables?.[0]?.datasetSettings as EventHubsSettings;
		if (isTruthy(settings?.includeHeaders)) {
			return true;
		}
	}
	return false;
}

// Default Mapping behaviour is to copy source schema as target schema, in-case target is empty
// For Streaming target entity, default assumption can't hold true always
export function isSchemaRequiredEvenForFirstRun(targetConnection?: MappingTargetConnectionInfo): boolean {
	return isStreamingSource(targetConnection?.tables?.[0]?.datasetType);
}

export function isPrimaryKeySelectionSupportedForDatasetType(connectionInfo: MappingTargetConnectionInfo[]): boolean {
	return supportedPrimaryKeyTargetOptions.has(connectionInfo?.[0]?.tables?.[0]?.datasetType);
}

export function isCosmosDBSource(connectionInfo: MappingSourceConnectionInfo[]): boolean {
	return DatasetFormatType.CosmosDB.match(connectionInfo?.[0]?.tables?.[0]?.datasetType)!= null;
}

function isLinkedServicePublished(ls: LinkedService): boolean {
	return !ModelMarker.isNew(ls);
}

function toOriginTypeName(dataFlowColumnType: IDataFlowDataType): IOriginDataTypeName {
	let decimalPrecision: number;
	let decimalScale: number;

	if (!!dataFlowColumnType &&
		dataFlowColumnType.typeString &&
		dataFlowColumnType.typeString.toLowerCase() === DecimalDataFlowType.TYPE.toLowerCase()) {
		decimalPrecision = (dataFlowColumnType as DecimalDataFlowType).precision;
		decimalScale = (dataFlowColumnType as DecimalDataFlowType).scale;
	}

	return {
		typeName: dataFlowColumnType?.typeString,
		precision: decimalPrecision,
		scale: decimalScale,
		isComplexType: false,
		isNullable: false
	};
}

function getCommonTransformationOptions(): Array<ISelectOption<ITransformationMethodOption>> {
	return [
		{
			label: stringResources.Direct,
			value: {
				label: stringResources.Direct,
				mappingType: MappingType.DIRECT
			}
		}
	];
}

function getAggregateTransformationOptions(): Array<ISelectOption<ITransformationMethodOption>> {
	return [
		{
			label: stringResources.Sum,
			value: {
				label: stringResources.Sum,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.SUM
			}
		},
		{
			label: stringResources.MinimumLabel,
			value: {
				label: stringResources.MinimumLabel,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.MIN
			}
		},
		{
			label: stringResources.MaximumLabel,
			value: {
				label: stringResources.MaximumLabel,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.MAX
			}
		},
		{
			label: stringResources.First,
			value: {
				label: stringResources.First,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.FIRST
			}
		},
		{
			label: stringResources.Last,
			value: {
				label: stringResources.Last,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.LAST
			}
		},
		{
			label: stringResources.DataflowDebugStatistics_StandardDeviation,
			value: {
				label: stringResources.DataflowDebugStatistics_StandardDeviation,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.STDDEV
			}
		},
		{
			label: stringResources.AverageLabel,
			value: {
				label: stringResources.AverageLabel,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.AVG
			}
		},
		{
			label: stringResources.Monitoring_DataFlowMean,
			value: {
				label: stringResources.Monitoring_DataFlowMean,
				mappingType: MappingType.AGGREGATE,
				dataflowFunction: DataFlowExpressionFunctions.MEAN
			}
		}
	];
}

function getDerivedTransformationOptions(): Array<ISelectOption<ITransformationMethodOption>> {
	return [
		{
			label: stringResources.Trim,
			value: {
				label: stringResources.Trim,
				mappingType: MappingType.DERIVED,
				dataflowFunction: DataFlowExpressionFunctions.TRIM
			}
		},
		{
			label: stringResources.Upper,
			value: {
				label: stringResources.Upper,
				mappingType: MappingType.DERIVED,
				dataflowFunction: DataFlowExpressionFunctions.UPPER
			}
		},
		{
			label: stringResources.Lower,
			value: {
				label: stringResources.Lower,
				mappingType: MappingType.DERIVED,
				dataflowFunction: DataFlowExpressionFunctions.LOWER
			}
		},
		{
			label: stringResources.Advanced,
			value: {
				label: stringResources.Advanced,
				mappingType: MappingType.ADVANCED
			},
			selectedOptTooltip: stringResources.ADFDataMapper_Advanced_Mapping_Tooltip
		}
	];
}

